import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { StatusBadge } from "./StatusBadge";
import { PhaseProgressBar } from "./PhaseProgressBar";
import type { IngestionPhase, PhaseKey, SubStreamKey } from "../types";

export const PhaseCard = ({ phase }: { phase: IngestionPhase }) => {
  const { t } = useTranslation(["onboarding"]);

  const title: Record<PhaseKey, string> = {
    recent: t(($) => $.onboarding["Recent performance"]),
    backfill: t(($) => $.onboarding["2-year history backfill"]),
    enrichment: t(($) => $.onboarding["Enrichment"]),
  };

  const subtitle: Record<PhaseKey, string> = {
    recent: t(($) => $.onboarding["The last 90 days — enough to start working."]),
    backfill: t(($) => $.onboarding["Unlocks comparisons, seasonality, and long-term trends."]),
    enrichment: t(
      ($) =>
        $.onboarding[
          "Page views and search query performance. These take longer and keep loading in the background after your core data is in."
        ],
    ),
  };

  const unlocks: Record<PhaseKey, string[]> = {
    recent: [
      t(($) => $.onboarding["Sales, units & revenue"]),
      t(($) => $.onboarding["Catalog & inventory"]),
      t(($) => $.onboarding["Advertising & attribution"]),
      t(($) => $.onboarding["Margin & ACOS"]),
    ],
    backfill: [
      t(($) => $.onboarding["Year-over-year comparisons"]),
      t(($) => $.onboarding["Seasonal & long-term trends"]),
      t(($) => $.onboarding["Full period-over-period"]),
    ],
    enrichment: [
      t(($) => $.onboarding["Traffic & conversion"]),
      t(($) => $.onboarding["Search query performance reports"]),
    ],
  };

  const subStreamLabel: Record<SubStreamKey, string> = {
    page_views: t(($) => $.onboarding["Page views & traffic"]),
    search_query: t(($) => $.onboarding["Search query performance"]),
  };

  return (
    <VStack
      alignItems="stretch"
      gap="medium"
      backgroundColor="white"
      border="1px solid {colors.border.default}"
      borderRadius="large"
      padding="large"
    >
      <HStack alignItems="flex-start" justifyContent="space-between" gap="medium">
        <Box>
          <Box fontSize="large" fontWeight="bold" color="text.heading">
            {title[phase.key]}
          </Box>
          <Box fontSize="small" color="text.secondary" marginTop="2px">
            {subtitle[phase.key]}
          </Box>
        </Box>
        <StatusBadge status={phase.status} />
      </HStack>

      {phase.delayed && (
        <Box fontSize="small" color="text.secondary" fontStyle="italic">
          {t(
            ($) =>
              $.onboarding["This is taking a little longer than usual — no action needed; it'll finish automatically."],
          )}
        </Box>
      )}

      {phase.percent != null && !phase.delayed && (
        <VStack alignItems="stretch" gap="xsmall">
          <PhaseProgressBar percent={phase.percent} />
          <Box alignSelf="flex-end" fontSize="small" fontWeight="semibold" color="text.secondary">
            {phase.percent}%
          </Box>
        </VStack>
      )}

      {phase.subStreams && (
        <VStack alignItems="stretch" gap="small">
          {phase.subStreams.map((sub) => (
            <Flex key={sub.key} justifyContent="space-between" alignItems="center" fontSize="small">
              <span className={css({ fontWeight: "semibold", color: "text.heading" })}>{subStreamLabel[sub.key]}</span>
              {sub.status === "not_applicable" ?
                <HStack gap="xsmall" color="text.secondary">
                  <span>{t(($) => $.onboarding["N/A"])}</span>
                  <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                    {t(
                      ($) =>
                        $.onboarding[
                          "We request these reports for products with recent sales or ad traffic. Nothing qualified for this account, so there's nothing to load."
                        ],
                    )}
                  </HoverInfo>
                </HStack>
              : sub.percent != null ?
                <span className={css({ color: "text.secondary" })}>{sub.percent}%</span>
              : <span className={css({ color: "text.secondary", fontStyle: "italic" })}>
                  {t(($) => $.onboarding["Coming soon"])}
                </span>
              }
            </Flex>
          ))}
        </VStack>
      )}

      <Flex flexWrap="wrap" gap="xsmall" rowGap="2px">
        {unlocks[phase.key].map((unlock) => (
          <HStack key={unlock} gap="xsmall" fontSize="small" color="text.secondary" marginRight="medium">
            <FaCheck size={10} className={css({ color: "green.600" })} />
            <span>{unlock}</span>
          </HStack>
        ))}
      </Flex>
    </VStack>
  );
};
